import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import type { GapsScopedShiftGroup, GapsStaffPlacement } from "../gapsGroupSheets";
import { findGroupByKey, findStaffPlacement } from "../gapsGroupSheets";
import { GAPS_DAY_LABEL_FORMAT, joinLabelParts } from "../gapsLabels";
import type { GapsHomeModel } from "../useGapsDayModel";
import { closeGapsSheetIfCurrent, type GapsSheetIntentOf, type GapsSheetKind } from "../useGapsSheet";
import { GapsCoverPromptStep } from "./GapsCoverPromptStep";
import { GapsRemovalStep } from "./GapsRemovalStep";
import { GapsSheetFrame } from "./GapsSheetFrame";
import { GapsStaffActionsStep } from "./GapsStaffActionsStep";
import { GapsStaffHistoryStep } from "./GapsStaffHistoryStep";
import { GapsStaffHoursStep } from "./GapsStaffHoursStep";
import { GapsStaffLocationStep } from "./GapsStaffLocationStep";
import { GapsStaffMoreStep } from "./GapsStaffMoreStep";
import { GapsStaffNoteStep } from "./GapsStaffNoteStep";
import { type GapsRemovalOutcome, GapsRemovalSource, type GapsSavingFlag, GapsStaffStep } from "./gapsSheetTypes";
import { staffHoursLabel } from "./staffSheetCopy";
import { useOpeningStep } from "./useOpeningStep";
import { useSheetOpening } from "./useSheetOpening";

type Props = {
	/** The store's own intent object for the shift whose sheet is up, or `null` when none is. */
	target: GapsSheetIntentOf<GapsSheetKind.StaffActions> | null;
	home: GapsHomeModel;
	/** Opens the post sheet prefilled from the group the removal left short (P3-4). */
	onPostShift: (groupKey: string) => void;
};

/** The sheet root's test id per step: each step is its own sheet to a test, even though one drawer stays open. */
const SHEET_TEST_ID: Record<GapsStaffStep, string> = {
	[GapsStaffStep.Actions]: "gaps-staff-actions-sheet",
	[GapsStaffStep.More]: "gaps-staff-more-sheet",
	[GapsStaffStep.Note]: "gaps-staff-note-sheet",
	[GapsStaffStep.Location]: "gaps-change-location-sheet",
	[GapsStaffStep.Hours]: "gaps-change-hours-sheet",
	[GapsStaffStep.Remove]: "gaps-removal-sheet",
	[GapsStaffStep.Cover]: "gaps-cover-prompt-sheet",
	[GapsStaffStep.History]: "gaps-staff-history-sheet",
};

/** The page the sheet shows, and the removal the cover step reports on. */
type StaffStepState = { step: GapsStaffStep; outcome: GapsRemovalOutcome | null };

/** Where every opening starts: the row's actions. */
const FIRST_STEP: StaffStepState = { step: GapsStaffStep.Actions, outcome: null };

/**
 * A staff row's sheet: its actions, and every step they lead to, in ONE drawer that stays open while the content
 * turns over (`SheetStep`). Replaces the PR 2 tap that left the home for the classic shift page, which offered the
 * same things (room, time, charge, trainee, removal) on a full-screen form away from the day's gaps.
 *
 * The person is looked up by shift id in the UNNARROWED day (`home.dayGroups`) on every render, so each step acts on
 * the shift as it now stands — a charge toggle is visible the moment it lands. The last placement found is kept for
 * two moments the day no longer lists the shift: the sheet's exit animation after a close, and the cover prompt after
 * a removal, which reads its group by key instead.
 *
 * The page belongs to the opening (`useOpeningStep`): a removal or a save that finishes after the user closed this sheet
 * and opened another never turns the newer one's page — no cover prompt for an old removal inside a new opening.
 */
export const GapsStaffSheet: FC<Props> = ({ target, home, onPostShift }) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { shown, openingKey } = useSheetOpening(target);
	const shiftId = target === null ? null : target.shiftId;
	// Closes THIS opening only: a save that finishes after the user moved on must not close what they moved to.
	const onClose = useCallback((): void => {
		if (shown !== null) closeGapsSheetIfCurrent(shown);
	}, [shown]);
	const live = useMemo(() => (shiftId === null ? undefined : findStaffPlacement(home.dayGroups, shiftId)), [home.dayGroups, shiftId]);
	const [retained, setRetained] = useState<GapsStaffPlacement<GapsScopedShiftGroup> | undefined>(undefined);
	useEffect(() => {
		if (live !== undefined) setRetained(live);
	}, [live]);
	// DEFAULT: the last placement found, for the two moments the day no longer lists the shift (see above).
	const placement = live ?? retained;
	// The More step's flag write in flight, held HERE so it outlives the step: leaving More mid-toggle and coming back
	// still shows that row saving and keeps the others off, instead of a fresh step that looks idle and takes a tap the
	// flag hook would refuse. Keyed by shift, so a write still finishing for one person never marks another's row.
	const [saving, setSaving] = useState<{ shiftId: string; flag: GapsSavingFlag } | null>(null);
	const handleSavingChange = useCallback((savingShiftId: string, flag: GapsSavingFlag, isSaving: boolean): void => {
		setSaving((previous) => {
			if (isSaving) return { shiftId: savingShiftId, flag };
			return previous !== null && previous.shiftId === savingShiftId && previous.flag === flag ? null : previous;
		});
	}, []);

	// Every opening starts on the actions, and a close leaves the page alone: the drawer animates out on the step the
	// user was looking at, rather than flashing back to the actions on its way down.
	const { step: stepState, setStepFor } = useOpeningStep<StaffStepState>(openingKey, FIRST_STEP);
	const { step, outcome } = stepState;

	// Both made for THIS render's opening: a step component calls them after its own await, and by then the user may be
	// in another opening, whose page they must not turn.
	const goTo = useCallback(
		(next: GapsStaffStep): void => {
			if (!setStepFor(openingKey, { step: next, outcome: null })) return;
			track("gaps_staff_sheet_step_opened", { department_id: chosenDepartment.department_id, shift_id: shiftId, step: next });
		},
		[chosenDepartment.department_id, openingKey, setStepFor, shiftId, track],
	);

	const handleRemoved = useCallback(
		(removal: GapsRemovalOutcome): void => {
			setStepFor(openingKey, { step: GapsStaffStep.Cover, outcome: removal });
		},
		[openingKey, setStepFor],
	);

	// Our own removal takes the shift off the day before the cover step takes over, so the removal and cover steps
	// stay open without it; every other step needs the shift to still be there.
	const outlivesShift = step === GapsStaffStep.Remove || step === GapsStaffStep.Cover;
	const isOpen = shiftId !== null && (live !== undefined || outlivesShift);

	// A shift that vanished while its sheet was up for another reason (removed elsewhere, a refresh) has nothing left
	// to act on, so the sheet closes rather than offering actions on a shift that no longer exists.
	useEffect(() => {
		if (shiftId !== null && live === undefined && !outlivesShift) onClose();
	}, [shiftId, live, outlivesShift, onClose]);

	// Nothing to draw before the first placement is found; the drawer mounts open, so its entrance still plays.
	if (placement === undefined) return null;

	const { group, entry } = placement;
	const shift = entry.slot.nurseInShift;
	const name = entry.slot.name;
	const hoursLabel = staffHoursLabel(entry, group.timeLabel);
	// The home's own (narrowed) numbers where it lists the group — the header the user returns to — else building-wide.
	const displayedGroup = findGroupByKey(home.groups, home.dayGroups, group.key);

	const renderStep = () => {
		switch (step) {
			case GapsStaffStep.Actions:
				return (
					<GapsStaffActionsStep
						entry={entry}
						name={name}
						jobLabel={group.jobLabel}
						timeLabel={group.timeLabel}
						hoursLabel={hoursLabel}
						onStep={goTo}
						onClose={onClose}
					/>
				);
			case GapsStaffStep.More:
				return (
					<GapsStaffMoreStep
						nurseInShift={shift}
						name={name}
						saving={saving !== null && saving.shiftId === shift.id ? saving.flag : null}
						onSavingChange={handleSavingChange}
						onStep={goTo}
					/>
				);
			case GapsStaffStep.History:
				return <GapsStaffHistoryStep nurseInShift={shift} name={name} onStep={goTo} onClose={onClose} />;
			case GapsStaffStep.Note:
				return (
					<GapsStaffNoteStep
						nurseInShift={shift}
						name={name}
						context={joinLabelParts([`${group.jobLabel} ${group.timeLabel}`, shift.startTime.toFormat(GAPS_DAY_LABEL_FORMAT)])}
						onStep={goTo}
					/>
				);
			case GapsStaffStep.Location:
				return (
					<GapsStaffLocationStep
						nurseInShift={shift}
						name={name}
						units={group.locations}
						currentLocation={entry.locationName}
						onStep={goTo}
						onClose={onClose}
					/>
				);
			case GapsStaffStep.Hours:
				return <GapsStaffHoursStep nurseInShift={shift} name={name} hoursLabel={hoursLabel} onStep={goTo} onClose={onClose} />;
			case GapsStaffStep.Remove:
				return (
					<GapsRemovalStep
						nurseInShift={shift}
						name={name}
						group={group}
						hoursLabel={hoursLabel}
						totalsBefore={displayedGroup === undefined ? null : { actual: displayedGroup.actual, required: displayedGroup.required }}
						source={GapsRemovalSource.StaffRow}
						onBack={() => goTo(GapsStaffStep.Actions)}
						onRemoved={handleRemoved}
					/>
				);
			case GapsStaffStep.Cover:
				return outcome === null ? null : (
					<GapsCoverPromptStep
						outcome={outcome}
						group={findGroupByKey(home.groups, home.dayGroups, outcome.groupKey)}
						onPostShift={onPostShift}
						onClose={onClose}
					/>
				);
		}
	};

	return (
		<GapsSheetFrame open={isOpen} onClose={onClose} dataTestId={SHEET_TEST_ID[step]} fullScreen={step === GapsStaffStep.History}>
			{renderStep()}
		</GapsSheetFrame>
	);
};
